<template>
    <p v-if="($slots.label || $attrs.label) && ['button', 'input', 'avatar'].includes(groupType)" class="tw:font-semibold tw:text-lg">
        <slot name="label">{{ $attrs.label }}</slot>
    </p>
    <!-- ButtonGroup for buttons -->
    <ButtonGroup v-if="groupType === 'button'" v-bind="$attrs">
        <slot />
    </ButtonGroup>
    <InputGroup v-else-if="groupType === 'input'" v-bind="$attrs">
        <template v-for="(child, index) in processedSlots" :key="index">
            <InputGroupAddon v-if="child.isAddon">
                <component :is="child.vnode" />
            </InputGroupAddon>
            <component :is="child.vnode" v-else />
        </template>
    </InputGroup>

    <AvatarGroup v-else-if="groupType === 'avatar'" v-bind="$attrs">
        <slot />
    </AvatarGroup>

    <div v-else class="field control tw:mb-0!">
        <p v-if="$slots.label || $attrs.label" class="tw:font-semibold tw:text-lg">
            <slot name="label">{{ $attrs.label }}</slot>
        </p>
        <div v-if="hasControlChild" class="field has-addons">
            <slot />
        </div>
        <slot v-else />
    </div>
    <span v-if="$attrs.message" class="tw:text-base tw:block" :class="messageClass">{{ $attrs.message }}</span>
</template>

<script>
/**
 * EField - Abstraction wrapper around PrimeVue group components to recreate Buefy's b-field functionality
 *
 * Usage:
 *   <e-field label="Username" message="Enter your username">
 *     <e-input v-model="username" />
 *   </e-field>
 *
 *   <e-field>
 *     <e-button>Save</e-button>
 *     <e-button>Cancel</e-button>
 *   </e-field>
 *
 * Slots:
 *   - default: The field content (inputs, buttons, etc.)
 *   - label: Custom label content (PrimeVue mode only). Falls back to label prop if not provided.
 *
 *   Example with label slot:
 *   <e-field>
 *     <template #label>Username <e-icon icon="info" /></template>
 *     <e-input v-model="username" />
 *   </e-field>
 *
 * Buefy Mode:
 * - If e-field contains a child with class="control", it will automatically
 *   wrap the content in <div class="field has-addons"> for proper Bulma styling.
 *
 * PrimeVue Mode:
 * - Automatically detects slot content and renders the appropriate group component:
 *   - ButtonGroup: when slot contains e-button components
 *   - InputGroup: when slot contains e-input or input-like components
 *   - AvatarGroup: when slot contains e-avatar components
 *
 * All Buefy b-field props and PrimeVue group component props are supported.
 */
export default {
    name: 'EField',
    inheritAttrs: false,
    computed: {
        groupType() {
            const slots = this.$slots.default?.() || [];

            const flattenedChildren = [];

            slots.forEach(vnode => {
                if (!vnode.type) {
                    return;
                }

                const hasControlClass = this.hasControlClass(vnode);

                if (hasControlClass && vnode.children) {
                    // Extract children from control wrapper
                    const children = Array.isArray(vnode.children) ? vnode.children : [vnode.children];
                    flattenedChildren.push(...children.filter(c => c && typeof c === 'object' && c.type));
                } else if (typeof vnode.type === 'object' || typeof vnode.type === 'string') {
                    flattenedChildren.push(vnode);
                }
            });

            if (flattenedChildren.length <= 1) {
                return null;
            }

            // Check for button components
            if (
                flattenedChildren.some(vnode => {
                    const componentName = this.getComponentName(vnode);
                    return componentName === 'EButton' || componentName === 'Button';
                })
            ) {
                return 'button';
            }

            // Check for input components
            if (
                flattenedChildren.some(vnode => {
                    const componentName = this.getComponentName(vnode);
                    return this.isInputComponent(componentName);
                })
            ) {
                return 'input';
            }

            // Check for avatar components
            if (
                flattenedChildren.some(vnode => {
                    const componentName = this.getComponentName(vnode);
                    return componentName === 'EAvatar' || componentName === 'Avatar';
                })
            ) {
                return 'avatar';
            }

            return null;
        },

        hasControlChild() {
            const slots = this.$slots.default?.() || [];

            return slots.some(vnode => {
                return this.hasControlClass(vnode);
            });
        },
        processedSlots() {
            const slots = this.$slots.default?.() || [];
            const result = [];

            slots.forEach(vnode => {
                if (!vnode.type) {
                    return;
                }

                const hasControlClass = this.hasControlClass(vnode);

                if (hasControlClass && vnode.children) {
                    const children = Array.isArray(vnode.children) ? vnode.children : [vnode.children];
                    children.forEach(child => {
                        if (child && typeof child === 'object') {
                            const componentName = this.getComponentName(child);
                            const isInput = this.isInputComponent(componentName);
                            result.push({ vnode: child, isAddon: !isInput });
                        }
                    });
                } else {
                    const componentName = this.getComponentName(vnode);
                    const isInput = this.isInputComponent(componentName);

                    result.push({ vnode, isAddon: !isInput });
                }
            });

            return result;
        },

        messageClass() {
            const { type } = this.$attrs;
            if (!type) return '';

            if (type === 'is-danger') return 'tw:text-red-600';
            if (type === 'is-warning') return 'tw:text-yellow-600';
            if (type === 'is-success') return 'tw:text-green-600';
            if (type === 'is-info') return 'tw:text-blue-600';

            return '';
        },
    },

    methods: {
        getComponentName(vnode) {
            if (!vnode.type) {
                return null;
            }
            // Check for name property
            if (vnode.type.name) {
                return vnode.type.name;
            }
            // Check for __name property (used in some Vue 3 builds)
            if (vnode.type.__name) {
                return vnode.type.__name;
            }
            // For string types (native elements)
            if (typeof vnode.type === 'string') {
                return vnode.type;
            }
            return null;
        },
        hasControlClass(vnode) {
            const classes = vnode.props?.class || '';
            return (
                (typeof classes === 'string' && classes.includes('control')) ||
                (Array.isArray(classes) && classes.includes('control'))
            );
        },
        isInputComponent(componentName) {
            return (
                componentName === 'EInput' ||
                componentName === 'InputText' ||
                componentName === 'InputNumber' ||
                componentName === 'Password'
            );
        },
    },
};
</script>
